import ApiClient from '@afosto/api/ApiClient';

class Fulfilment {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Add a new fulfilment location
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create a new fulfilment location
   * @returns {Promise}
   */
  createFulfilmentLocation(body, options = {}) {
    return this.apiClient.request({
      url: `/lcs/locations`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Reorder the positions within a location
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Updates the position order for a location
   * @returns {Promise}
   */
  deletePositions(id, body, options = {}) {
    return this.apiClient.request({
      url: `/lcs/locations/${id}/positions`,
      method: 'delete',
      data: body,
      ...options,
    });
  }

  /**
   * Get a fulfilment location
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a fulfilment location
   * @returns {Promise}
   */
  getFulfilmentLocation(id, options = {}) {
    return this.apiClient.request({
      url: `/lcs/locations/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a service point
   *
   * @param {string} carrier
   * @param {string} countryCode
   * @param {string} id
   * @param {Object} query - The optional path parameters
   * @param {string} [query.channelId]
   * @param {Object} options - The additional request options
   * @description Returns a single service
   * @returns {Promise}
   */
  getServicePoint(id, carrier, countryCode, query = {}, options = {}) {
    const params = {
      channel_id: query.channelId,
      country_code: countryCode,
    };

    return this.apiClient.request({
      url: `/lcs/points/${carrier}/${id}`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get list of fulfilment locations
   *
   * @param {Object} query - The optional path parameters
   * @param {string} [query.addressId]
   * @param {string} [query.isDisabled]
   * @param {Object} options - The additional request options
   * @description Returns a list of fulfilment locations
   * @returns {Promise}
   */
  listFulfilmentLocations(query = {}, options = {}) {
    const params = {
      address_id: query.addressId,
      is_disabled: query.isDisabled,
    };

    return this.apiClient.request({
      url: `/lcs/locations`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get list of sku&#39;s that are listed for the fulfilment location
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a list of fulfilment locations
   * @returns {Promise}
   */
  listLocationSkus(id, options = {}) {
    return this.apiClient.request({
      url: `/lcs/locations/${id}/skus`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get list of positions within a fulfilment location
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a list of fulfilment locations
   * @returns {Promise}
   */
  listPositions(id, options = {}) {
    return this.apiClient.request({
      url: `/lcs/locations/${id}/positions`,
      method: 'get',
      ...options,
    });
  }

  /**
   * List service points
   *
   * @param {string} carrier
   * @param {string} countryCode
   * @param {string} postalCode
   * @param {Object} query - The optional path parameters
   * @param {string} [query.channelId]
   * @param {Object} options - The additional request options
   * @description List all service points
   * @returns {Promise}
   */
  listServicePoints(carrier, countryCode, postalCode, query = {}, options = {}) {
    const params = {
      channel_id: query.channelId,
      country_code: countryCode,
      postal_code: postalCode,
    };

    return this.apiClient.request({
      url: `/lcs/points/${carrier}`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Search a set of positions
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Searches a set of positions based on sku&#39;s
   * @returns {Promise}
   */
  searchPositions(id, body, options = {}) {
    return this.apiClient.request({
      url: `/lcs/locations/${id}/positions/search`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Update fulfilment location
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Updates a location
   * @returns {Promise}
   */
  updateFulfilmentLocation(id, body, options = {}) {
    return this.apiClient.request({
      url: `/lcs/locations/${id}`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Reorder the positions within a location
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Updates the position order for a location
   * @returns {Promise}
   */
  updatePositionOrder(id, body, options = {}) {
    return this.apiClient.request({
      url: `/lcs/locations/${id}/positions`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Update one or more sku&#39;s for the location
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Updates the position order for a location
   * @returns {Promise}
   */
  upsertLocationSkus(id, body, options = {}) {
    return this.apiClient.request({
      url: `/lcs/locations/${id}/skus`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * View position
   *
   * @param {string} code
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a single positino
   * @returns {Promise}
   */
  viewPosition(id, code, options = {}) {
    return this.apiClient.request({
      url: `/lcs/locations/${id}/positions/${code}`,
      method: 'get',
      ...options,
    });
  }
}

export const fulfilment = new Fulfilment();

export default Fulfilment;
